import { isNum, num, rupees } from "./fmt";
import { engineState } from "../../lib/riskMath";

/*
 * AT A GLANCE — the plain-language readers behind the Buyer Funnel's gates
 * (2026-10-01, folded into the funnel 2026-10-06). Each answers one question
 * a trader asks first, in words rather than indicator values:
 *
 *   1. Is the market open, and how long is left to take a trade?
 *   2. Is the market moving enough? (the atr_pct gate every live rule uses —
 *      the one regime measure the research found reliable)
 *   4. What are my rules doing? (engine state, open positions, today's P&L)
 *
 * Pure functions over payloads the Dashboard already holds. No fetching.
 */

const PHASE_WORDS = {
  PRE_OPEN: ["Not open yet", "slate"],
  OPENING_NOISE: ["Opening minutes", "amber"],
  ENTRY_WINDOW: ["Trading window open", "emerald"],
  MIDDAY_THETA: ["Midday · slow market", "amber"],
  NO_NEW_ENTRIES: ["No new trades now", "rose"],
  SQUARE_OFF: ["Closing positions", "rose"],
  CLOSED: ["Market closed", "slate"],
};

/* 1 · market open? */
export function marketTile(view, session) {
  if (view?.sessionClosed) {
    return {
      answer: view.closedLabel === "Market holiday" ? "Holiday" : "Closed",
      tone: "slate",
      detail: "Numbers below are the last snapshot — for planning only.",
    };
  }
  const [answer, tone] = PHASE_WORDS[session?.phase] || ["Open", "cyan"];
  const left = session?.minutes_to_cutoff;
  const detail = isNum(left) && Number(left) > 0
    ? `${num(left)} min left to take new trades.`
    : session ? "Entry cutoff has passed — open trades are only managed." : "Waiting for the session feed.";
  return { answer, tone, detail };
}

/* 2 · moving enough? — the atr_pct gate, as a multiple of its minimum */
export function movementTile(edge, view) {
  const gates = edge?.magnitude?.available ? (edge.magnitude.gates || []) : [];
  const atr = gates.find((g) => g.key === "atr_pct") || gates[0];
  const ratio = atr && isNum(atr.ratio) ? Number(atr.ratio) : null;
  if (view?.sessionClosed) {
    return { answer: "—", tone: "slate", ratio, detail: "Measured only while the market is open." };
  }
  if (ratio == null) return { answer: "Unknown", tone: "amber", ratio: null, detail: "Needs live 5-minute candles to measure." };
  const reference = atr?.source === "reference";
  const who = reference ? "a NIFTY reference level (no rule trades this index)" : "the minimum the rules need";
  // the rules' own verdict when the payload carries it (either side's gate passing
  // counts: #107 gates band width on CALLs only), else the atr ratio alone
  const passed = typeof edge?.magnitude?.gate_passed === "boolean" ? edge.magnitude.gate_passed : ratio >= 1;
  if (passed) {
    const sideNote = edge?.magnitude?.gate_passed_call === false ? " Calls still need wider bands." : edge?.magnitude?.gate_passed_put === false ? " Puts still need wider bands." : "";
    return { answer: "Yes", tone: reference ? "cyan" : "emerald", ratio, detail: `Moving ${num(ratio, 1)}× ${who}. Big moves are possible.${sideNote}` };
  }
  return { answer: "Too quiet", tone: "rose", ratio, detail: `Only ${num(ratio * 100)}% of ${who}. Option buyers lose in quiet markets.` };
}

/* 2b · today's regime — the backdrop (quiet / normal / active stretch) and the odds of a
 * bigger-than-usual day. INFORMATION ONLY: research 2026-10-05 found the size of the day is
 * predictable by 09:45 (not its direction) and that no regime gate helps the live rule, so
 * nothing trades on this — it sets expectations (a quiet stretch means idle, flat months). */
export function regimeLines(regime, view) {
  if (!regime?.available) return null;
  const out = {};
  const v = regime.vol_level;
  if (v?.available) {
    const [label, tone] = { QUIET: ["Quiet stretch", "amber"], ACTIVE: ["Active stretch", "emerald"] }[v.state] || ["Normal stretch", "slate"];
    out.backdrop = { label, tone, detail: v.detail };
  }
  const e = regime.expansion;
  if (e?.available && !view?.sessionClosed && isNum(e.prob_pct)) {
    const tone = e.lean === "BIG_MOVE_LIKELY" ? "emerald" : e.lean === "QUIET_DAY_LIKELY" ? "amber" : "slate";
    out.odds = { label: `Big-day odds ${num(e.prob_pct)}%`, tone, detail: e.detail, early: e.stage !== "OPENING_RANGE" };
  }
  return out.backdrop || out.odds ? out : null;
}

/* 4 · my rules — engine state, open positions on this index, today's P&L */
export function rulesTile(status, rules, activeIndex) {
  const state = engineState(status);
  const open = (status?.open_trades || []).filter((t) => t.underlying === activeIndex);
  const openPnl = open.reduce((s, t) => s + (Number(t.net_unrealized_pnl) || 0), 0);
  const today = isNum(status?.today_pnl) ? Number(status.today_pnl) : null;
  const n = rules?.length || 0;
  if (state === "halted") return { answer: "Stopped", tone: "rose", detail: "Kill switch is ON — no rule can open a trade.", open, openPnl, today };
  if (open.length) {
    return {
      answer: `In ${open.length} trade${open.length > 1 ? "s" : ""}`, tone: openPnl >= 0 ? "emerald" : "rose",
      detail: `Open P&L ${openPnl >= 0 ? "+" : "−"}${rupees(Math.abs(openPnl))} after costs.`, open, openPnl, today,
    };
  }
  if (!n) return { answer: "No rules", tone: "slate", detail: `No ACTIVE rule trades ${activeIndex === "BANKNIFTY" ? "Bank Nifty" : "Nifty"}.`, open, openPnl, today };
  if (state === "paused") return { answer: "Paused", tone: "amber", detail: "Autotrader is OFF — rules are read, nothing is ordered.", open, openPnl, today };
  return {
    answer: "Watching", tone: "cyan",
    detail: `${n} rule${n > 1 ? "s" : ""} armed, waiting for an entry signal.`, open, openPnl, today,
  };
}
